import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { isPrescriberCountry } from "../utils/helpers";
import CheckoutRegister from "../pages/CheckoutRegister/CheckoutRegister";
import ChooseTreatmentLandingPage from "../pages/ChooseTreatmentVariants/ChooseTreatmentLandingPage/ChooseTreatmentLandingPage";
import PaymentPageCallBack from "../pages/Payment/PaymentPageCallBack";
import Thankyou from "../pages/Thankyou/Thankyou";
import RepayPage from "../pages/Payment/Repay/RepayPage";
import CheckoutEvaluation from "../pages/CheckoutEvaluation/CheckoutEvaluation";
import LoadingModal from "../components/Modals/LoadingModal/LoadingModal";
import ServerError from "../components/ServerError/ServerError";
import PageTracker from "../components/PageTracker/PageTracker";
import HeaderController from "../components/Headers/HeaderController";
import ProtectedCustomerRoute from "./ProtectedCustomerRoute";
import Questionnaire from "../pages/Questionnaire/Questionnaire";
import ChooseTreatmentRouter from "../pages/ChooseTreatmentVariants/ChooseTreatmentRouter";
import MyAccountLayout from "../pages/MyAccount/MyAccountLayout";
import MyAccountMyOrders from "../pages/MyAccount/components/MyAccountMyOrders/MyAccountMyOrders";
import MyAccountSubscriptions from "../pages/MyAccount/components/MyAccountSubscriptions/MyAccountSubscriptions";
import MyAccountQuestionnaire from "../pages/MyAccount/components/MyAccountQuestionnaireDefaults/MyAccountQuestionnaireDefaults";
import MyAccountPersonalData from "../pages/MyAccount/components/MyAccountPersonalData/MyAccountPersonalData";
import MyAccountAskADoctor from "../pages/MyAccount/components/MyAccountAskADoctor/MyAccountAskADoctor";
import MyAccountNewsletter from "../pages/MyAccount/components/MyAccountNewsletter/MyAccountNewsletter";
import MyAccountChangePassword from "../pages/MyAccount/components/MyAccountChangePassword/MyAccountChangePassword";
import MyAccountOrderQuestionnaire from "../pages/MyAccount/components/MyAccountOrderQuestionnaire/MyAccountOrderQuestionnaire";
import MyAccountOrderDetailsDispatcher from "../pages/MyAccount/components/MyAccountOrderDetails/MyAccountOrderDetailsDispatcher";
import LoginPage from "../pages/LoginPage/LoginPage";
import ProtectedAuthRoutes from "./ProtectedAuthRoutes";
import RegistrationPage from "../pages/RegistrationPage/RegistrationPage";
import ProtectedMyAccount from "./ProtectedMyAccount";
import MyAccountOrderTreatmentInstructions from "../pages/MyAccount/components/MyAccountOrderTreatmentInstructions/MyAccountOrderTreatmentInstructions";
import ResetPasswordPage from "../pages/ResetPasswordPage/ResetPasswordPage";
import DeactivateAccountConfirmationPage from "../pages/DeactivateAccountConfirmationPage/DeactivateAccountConfirmationPage";
import ChangedEmailConfirmationPage from "../pages/ChangedEmailConfirmationPage/ChangedEmailConfirmationPage";
import ReorderLayout from "../pages/Reorder/ReorderLayout";
import MedicalDisclaimer from "../pages/Reorder/components/MedicalDisclaimer/MedicalDisclaimer";
import ChooseTreatmentReorder from "../pages/Reorder/components/ChooseTreatmentReorder/ChooseTreatmentReorder";
import ContactPage from "../pages/ContactPage/ContactPage";
import StartConsultation from "../pages/StartConsultation/StartConsultation";
import { ShippingPageProvider } from "../pages/v2/ShippingV2/providers/ShippingPageProvider";
import ProtectedOTC from "./ProtectedOTC";
import MyAccountDeliveryAddressV2 from "../pages/MyAccount/components/MyAccountDeliveryAddressV2/MyAccountDeliveryAddressV2";
import MyAccountStore from "../pages/MyAccount/components/MyAccountStore/MyAccountStore";
import MyAccountCareProfile from "../pages/MyAccount/components/MyAccountCareProfile/MyAccountCareProfile";
import { testLabStoreConfig } from "../pages/MyAccount/components/MyAccountStore/stores/testLabStoreConfig";
import OAuthCallback from "../components/OAuthCallback";
import CanceledFlowzzOrderPage from "../pages/CanceledFlowzzOrderPage/CanceledFlowzzOrderPage";
import Shipping from "../pages/v2/ShippingV2/ShippingV2";
import FunnelLayout from "../layouts/FunnelLayout";
import ChooseDoctorAndAppointment from "../pages/chooseDoctor/chooseDoctorAndAppointment";
import SchengenSuspendedQuestionnaire from "../pages/Questionnaire/SchengenSuspendedQuestionnaire";
import CustomerVerification from "../pages/CustomerVerification/CustomerVerification";
import WhatsAppRedirect from "../components/WhatsAppRedirect";
import PaymentPage from "../pages/Payment/PaymentPage";
import FunnelMentionRedirect from "./FunnelMentionRedirect";


/** HL-10315: UK old path redirects to ask-a-prescriber; other markets keep ask-a-doctor. */
function AskADoctorRoute() {
  const { country } = useParams<{ country: string }>();
  if (isPrescriberCountry(country)) {
    return <Navigate to="../ask-a-prescriber" replace />;
  }
  return <MyAccountAskADoctor />;
}

// The V2-era Care+ intro carousel is gone; old links continue at the treatment page.
function CarePlusIntroRoute() {
  const { country, language, category } = useParams<{ country: string; language: string; category: string }>();
  return <Navigate to={`/select_treatment/${country}/${language}/${category}${window.location.search}`} replace />;
}

function PagesRouter() {

  return (
    <Router>
      <PageTracker /> {/* Tracker component, listents for url changes, doesnt renders in return */}
      <Routes>
        <Route path="/questionnaire/:country/:language/schengen" element={<SchengenSuspendedQuestionnaire />} />
        <Route path="/questionnaire/:country/:language/:category" element={<FunnelMentionRedirect><Questionnaire /></FunnelMentionRedirect>} />
        <Route path="/select_treatment/:country/:language/:category" element={<ChooseTreatmentRouter />} />
        <Route path="/care_plus/:country/:language/:category" element={<CarePlusIntroRoute />} />
        {/* HL-11360: V3 has no Choose Plan route; the plan step renders inside Choose Treatment, which only
            rewrites the address bar to /choose_plan. HL-12777: the same element as /select_treatment, so
            browser Forward onto /choose_plan keeps the page mounted; a page load there redirects (see ChooseTreatmentRouter). */}
        <Route path="/choose_plan/:country/:language/:category" element={<ChooseTreatmentRouter />} />
        <Route path="/select_treatment_landing_page/:country/:language/:category" element={<ChooseTreatmentLandingPage />} />
        <Route path="/sign_up/:country/:language" element={<CheckoutRegister />} />
        <Route path="/evaluation/:country/:language" element={<CheckoutEvaluation />} />
        <Route path="/customer-verification/:country/:language" element={<CustomerVerification />} />
        <Route path="/shipping/:country/:language" element={<ProtectedOTC><ProtectedCustomerRoute><ShippingPageProvider><FunnelLayout headerStage="Shipping"><Shipping /></FunnelLayout></ShippingPageProvider></ProtectedCustomerRoute></ProtectedOTC>} />
        <Route path="/payment/:country/:language/:order_hash_id/" element={<ProtectedCustomerRoute shouldHaveCart={false}><PaymentPage /></ProtectedCustomerRoute>} />
        <Route path="/payment_page_callback" element={<PaymentPageCallBack />} />
        <Route path="/thank-you/:country/:language/:order_hash_id" element={<Thankyou />} />
        <Route path="/repay/:country/:language/:token" element={<ProtectedMyAccount><RepayPage /></ProtectedMyAccount>} />
        <Route path="/loading" element={<LoadingModal />} />
        <Route path="/server_error" element={<ServerError />} />
        <Route path="*" element={<> <HeaderController showSteps={false} stage={"Questionnaire"} /> <ServerError status={404} /></>} />

        {/* OAuth callback route */}
        <Route path="/auth/callback" element={<OAuthCallback />} />

        <Route path="/reorder/:order_hash_id/:country/:language/" element={<ProtectedCustomerRoute shouldHaveCart={false}> <ReorderLayout /> </ProtectedCustomerRoute>}>
          <Route path="choose_treatment" element={<ChooseTreatmentReorder />} />
          <Route path="medical_disclaimer" element={<MedicalDisclaimer />} />
        </Route>
        
        {/* WhatsApp redirect route */}
        <Route path="/:country/account/my-orders/details/:channel/:message_code/:order_hash_id" element={<WhatsAppRedirect />} />
        
        {/* My Account Routes:  */}

        <Route path="/:country/account" element={<ProtectedMyAccount> <MyAccountLayout /> </ProtectedMyAccount>}>

          <Route path="my-orders" element={<MyAccountMyOrders />} />
          <Route path="test-lab" element={<MyAccountStore config={testLabStoreConfig} />} />
          <Route path="my-orders/:order_hash_id/details" element={<MyAccountOrderDetailsDispatcher />} />
          <Route path="my-orders/:order_hash_id/prescription" element={<MyAccountOrderQuestionnaire />} />
          <Route path="my-orders/:order_hash_id/treatment-instructions" element={<MyAccountOrderTreatmentInstructions />} />

          <Route path="treatment-plans" element={<Navigate to="../orders-subscription" replace />} />

          <Route path="orders-subscription" element={<MyAccountSubscriptions showOneCardWithEditPanel={false} />} />
          <Route path="orders-subscription/:order_hash_id" element={<MyAccountSubscriptions showOneCardWithEditPanel={true} />} />

          <Route path="questionnaire-defaults" element={<MyAccountQuestionnaire />} />
          {/* <Route path="delivery-address" element={<MyAccountDeliveryAddress />} /> */}
          <Route path="delivery-address" element={<MyAccountDeliveryAddressV2 />} />
          <Route path="personal-data" element={<MyAccountPersonalData />} />
          {/* HL-10315: UK public path is ask-a-prescriber; other markets keep ask-a-doctor. */}
          <Route path="ask-a-prescriber" element={<MyAccountAskADoctor />} />
          <Route path="ask-a-doctor" element={<AskADoctorRoute />} />
          <Route path="newsletter" element={<MyAccountNewsletter />} />
          <Route path="change-password" element={<MyAccountChangePassword />} />
        </Route>
        {/* End of My Account Routes:  */}

        {/* Care profile is entered from My Account (Treatment Plans) but renders as its own standalone page, not inside MyAccountLayout's account-shell chrome */}
        <Route path="/:country/account/care-profile" element={<ProtectedMyAccount> <MyAccountCareProfile /> </ProtectedMyAccount>} />





        <Route path="/:country/contact" element={<ContactPage />} />
        <Route path="/start-consultation/:country/:language/:mention" element={<FunnelMentionRedirect><StartConsultation /></FunnelMentionRedirect>} /> 
        <Route path="/choose-doctor/:country/:language/:mention" element={<ChooseDoctorAndAppointment />} /> 


        <Route path="/:country/deactivate-account/:token" element={<ProtectedMyAccount><DeactivateAccountConfirmationPage /></ProtectedMyAccount>} />
        <Route path="/:country/change-email/:token" element={<ChangedEmailConfirmationPage />} />

        {/* Authentication Routes: */}
        <Route path="/:country/register" element={<ProtectedAuthRoutes> <RegistrationPage /> </ProtectedAuthRoutes>} />
        <Route path="/:country/login" element={<ProtectedAuthRoutes> <LoginPage /> </ProtectedAuthRoutes>} />
        <Route path="/:country/reset-password/:token" element={<ProtectedAuthRoutes> <ResetPasswordPage /> </ProtectedAuthRoutes>} />

        {/* End of Authentication Routes: */}

        <Route path="canceled-flowzz-order/:action_token/:customer_choice" element={<CanceledFlowzzOrderPage />} />

      </Routes>
    </Router>
  );
}

export default PagesRouter;
